import { Editor } from "primereact/editor";
import { useContext, useEffect, useRef, useState } from "react";
import CustomInput from "../../components/CustomInput";
import { firebaseService } from "../../../../services/api/firebaseService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

/* ================== HELPERS ================== */
const extractFirebaseImageUrls = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== "string") return [];

  const doc = new DOMParser().parseFromString(htmlContent, "text/html");
  return Array.from(doc.querySelectorAll("img"))
    .map((img) => img.getAttribute("src"))
    .filter((src) => src?.startsWith("https://storage.googleapis.com/"));
};

const AddBannerPopup = ({
  mode = "create",
  initial,
  handleAddPost,
  handleEditPost,
  closePopup,
}) => {
  const { token } = useContext(AuthContext);

  const [form, setForm] = useState({
    name: "",
    content: "",
    imageFile: null,
    imagePreview: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const fileInputRef = useRef(null);

  // lưu data cũ (edit)
  const [oldData, setOldData] = useState({
    txtUrl: "",
    bannerImageUrl: "",
    contentImageUrls: [],
  });

  /* ================== INIT ================== */
  useEffect(() => {
    if (!initial) {
      setForm({
        name: "",
        content: "",
        imageFile: null,
        imagePreview: "",
      });
      setOldData({
        txtUrl: "",
        bannerImageUrl: "",
        contentImageUrls: [],
      });
      return;
    }

    setForm({
      name: initial.name || "",
      content: initial.txtUrl || "",
      imageFile: null,
      imagePreview: initial.imagePreview || "",
    });

    setOldData({
      txtUrl: initial.txtUrl || "",
      bannerImageUrl: initial.imagePreview || "",
      contentImageUrls: extractFirebaseImageUrls(initial.txtUrl || ""),
    });
  }, [initial]);

  /* ================== IMAGE ================== */
  const onPickImage = () => fileInputRef.current?.click();

  const onImageChange = (file) => {
    if (!file) {
      setForm((s) => ({ ...s, imageFile: null, imagePreview: "" }));
      return;
    }
    const url = URL.createObjectURL(file);
    setForm((s) => ({ ...s, imageFile: file, imagePreview: url }));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith("image/")) onImageChange(f);
  };

  /* ================== CONTENT ================== */
  const handleContentChange = (e) => {
    setForm((s) => ({ ...s, content: e.htmlValue || "" }));
  };

  const processImagesInContent = async (html) => {
    if (!html) return html;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const imgs = doc.querySelectorAll("img");

    for (const img of imgs) {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("https://storage.googleapis.com/")) continue;

      if (src.startsWith("data:image/")) {
        const blob = await (await fetch(src)).blob();
        const file = new File([blob], `editor-${Date.now()}.png`, {
          type: blob.type,
        });
        const up = await firebaseService.upload_file(token, file);
        img.setAttribute("src", up.url);
      }
    }

    return doc.body.innerHTML;
  };

  /* ================== SUBMIT ================== */
  const onSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Vui lòng nhập tiêu đề banner.");
      return;
    }
    if (!form.content.trim()) {
      toast.error("Vui lòng nhập nội dung banner.");
      return;
    }
    if (mode === "create" && !form.imageFile) {
      toast.error("Vui lòng chọn hình ảnh banner.");
      return;
    }

    setSubmitting(true);

    try {
      const processedContent = await processImagesInContent(form.content);
      const newContentImages = extractFirebaseImageUrls(processedContent);

      let bannerImageUrl = oldData.bannerImageUrl;

      if (form.imageFile instanceof File) {
        const uploadRes = await firebaseService.upload_file(
          token,
          form.imageFile,
        );
        bannerImageUrl = uploadRes?.url;
        if (!bannerImageUrl) throw new Error("Upload ảnh banner thất bại");
      }

      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("txtUrl", processedContent);
      fd.append("thumbnail", bannerImageUrl);

      if (mode === "create") {
        await Promise.resolve(handleAddPost?.(fd));
        toast.success("Thêm banner thành công!");
      } else {
        await Promise.resolve(handleEditPost?.(fd));
        toast.success("Cập nhật banner thành công!");
      }

      /* ===== CLEANUP FIREBASE (EDIT) ===== */
      if (mode === "edit") {
        if (
          form.imageFile &&
          oldData.bannerImageUrl?.startsWith("https://storage.googleapis.com/")
        ) {
          await firebaseService.delete_file(token, oldData.bannerImageUrl);
        }

        const toDelete = oldData.contentImageUrls.filter(
          (u) => !newContentImages.includes(u),
        );
        for (const url of toDelete) {
          await firebaseService.delete_file(token, url);
        }
      }

      closePopup();
    } catch (err) {
      toast.error(
        err?.message ||
          `${mode === "create" ? "Thêm" : "Cập nhật"} banner thất bại.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={() => setShowCancelConfirm(true)}
        title={mode === "create" ? "Thêm banner mới" : "Chỉnh sửa banner"}
        maxWidth="960px"
        showCancelButton
        cancelButtonText="Hủy"
        primaryButtonText={
          submitting
            ? mode === "create"
              ? "Đang thêm..."
              : "Đang lưu..."
            : mode === "create"
              ? "Thêm"
              : "Lưu"
        }
        primaryButtonOnClick={onSubmit}
        primaryButtonVariant="primary"
      >
        {/* ===== BODY (UI GIỮ NGUYÊN) ===== */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="px-6 py-4 space-y-4">
            {/* IMAGE */}
            <div className="space-y-2">
              <strong className="text-gray-700">Hình ảnh banner:</strong>

              <div
                className="relative w-full aspect-[3/1] rounded-lg overflow-hidden border border-dashed border-gray-300 bg-gray-50 cursor-pointer"
                onClick={onPickImage}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
              >
                {form.imagePreview ? (
                  <img
                    src={form.imagePreview}
                    alt="Preview"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-gray-500">
                    <div className="text-center">
                      <div className="text-base font-medium">Chưa có ảnh</div>
                      <div className="text-xs opacity-80">
                        Nhấp để chọn ảnh hoặc kéo thả
                      </div>
                    </div>
                  </div>
                )}

                <div className="absolute inset-0 bg-black/10 opacity-0 hover:opacity-100 transition-opacity grid place-items-center">
                  <div className="px-4 py-2 rounded-lg bg-white/90 border shadow text-sm">
                    {form.imagePreview ? "Thay đổi ảnh" : "Thêm ảnh"}
                  </div>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => onImageChange(e.target.files?.[0] || null)}
              />
            </div>

            {/* TITLE */}
            <div className="flex flex-col gap-1">
              <strong className="text-gray-700">Tiêu đề:</strong>
              <CustomInput
                placeholder="Nhập tiêu đề banner"
                value={form.name}
                required
                onChange={(v) => setForm((s) => ({ ...s, name: v }))}
              />
            </div>

            {/* CONTENT */}
            <div className="flex flex-col gap-1">
              <strong className="text-gray-700">Nội dung:</strong>
              <Editor
                value={form.content}
                onTextChange={handleContentChange}
                style={{ height: 240 }}
              />
              <p className="text-xs text-gray-500">
                💡 Ảnh trong nội dung sẽ tự động upload Firebase khi lưu.
              </p>
            </div>
          </div>
        </form>
      </Modal>

      {/* CONFIRM CANCEL */}
      <ConfirmDeleteModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={closePopup}
        title="Xác nhận hủy"
        message="Những thay đổi của bạn sẽ không được lưu. Bạn có chắc chắn muốn hủy?"
      />
    </>
  );
};

export default AddBannerPopup;
